<template>
    <v-combobox
        ref="combobox"
        v-model="selectedTags"
        :items="tagSuggestions"
        :search-input.sync="searchInput"
        :loading="loading"
        :label="label"
        :placeholder="placeholder"
        :disabled="disabled"
        :dense="dense"
        :clearable="clearable"
        :outlined="outlined"
        :flat="flat"
        :hide-details="hideDetails"
        :background-color="backgroundColor"
        :prepend-inner-icon="prependInnerIcon"
        :required="required"
        :class="className"
        :error-messages="errorMessages"
        :autofocus="autofocus"
        item-text="text"
        item-value="value"
        return-object
        multiple
        chips
        deletable-chips
        :filter="filterTags"
        @update:search-input="onSearchInput"
    >
        <template slot="selection" slot-scope="{ item, index }">
            <span
                :key="`tag-${index}-${getTagName(item)}`"
                class="tag-input-selection"
                @mousedown.stop.prevent
            >
                <Tag
                    :tag="item"
                    size="small"
                    :closable="true"
                    @close="removeTag(item)"
                    chip-class="mr-1 mt-1"
                />
            </span>
        </template>
        <template slot="item" slot-scope="{ item }">
            <v-list-item 
                class="tag-suggestion-item"
                @click="selectTag(item)"
            >
                <Tag
                    :tag="item"
                    size="small"
                    chip-class="mr-1 mb-1"
                />
            </v-list-item>
        </template>
        <template slot="no-data">
            <v-list-item>
                <v-list-item-content>
                    <v-list-item-title>
                        {{
                            allowCreate
                                ? $translate("noTagsFound")
                                : $translate("noMatchingTags")
                        }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </template>
    </v-combobox>
</template>

<script>
import axios from "@/plugins/axios";
import Tag from "@/components/common/tag.vue";
import { debounce } from "lodash";

export default {
    name: "TagInput",
    components: {
        Tag,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        objectType: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        flat: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: [Boolean, String],
            default: false,
        },
        backgroundColor: {
            type: String,
            default: null,
        },
        className: {
            type: String,
            default: null,
        },
        prependInnerIcon: {
            type: String,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        errorMessages: {
            type: Array,
            default: () => [],
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        allowCreate: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            searchInput: null,
            loading: false,
            tagSuggestions: [],
            debouncedSearch: null,
        };
    },
    computed: {
        selectedTags: {
            get() {
                // Convert value to format expected by v-combobox
                const tags = this.value || [];
                return tags.map(tag => {
                    if (typeof tag === 'string') {
                        // Find color from suggestions
                        const color = this.getTagColor(tag);
                        return {
                            text: tag,
                            value: tag,
                            name: tag,
                            color: color
                        };
                    }
                    // Already an object
                    return {
                        text: tag.name || tag.text || tag.value || '',
                        value: tag.name || tag.text || tag.value || '',
                        name: tag.name || tag.text || tag.value || '',
                        color: tag.color || this.getTagColor(tag) || '#9E9E9E'
                    };
                });
            },
            set(value) {
                this.handleInput(value);
            },
        },
    },
    created() {
        this.debouncedSearch = debounce(this.fetchTagSuggestions, 300);
        this.loadInitialTags();
    },
    mounted() {
        if (this.autofocus) {
            this.$nextTick(() => {
                if (this.$refs.combobox && this.$refs.combobox.$el) {
                    const input = this.$refs.combobox.$el.querySelector('input');
                    if (input) {
                        input.focus();
                    }
                }
            });
        }
    },
    methods: {
        async loadInitialTags() {
            try {
                this.loading = true;
                const response = await axios.get("/api/tags/", {
                    params: {
                        object_type: this.objectType,
                    },
                });
                if (response.data && response.data.results) {
                    this.tagSuggestions = response.data.results.map(tag => ({
                        text: tag.name,
                        value: tag.name,
                        color: tag.color || '#9E9E9E'
                    }));
                } else {
                    this.tagSuggestions = [];
                }
            } catch (error) {
                console.error("Error loading tags:", error);
            } finally {
                this.loading = false;
            }
        },
        async fetchTagSuggestions() {
            if (!this.searchInput || this.searchInput.trim().length < 1) {
                this.loadInitialTags();
                return;
            }

            try {
                this.loading = true;
                const response = await axios.get("/api/tags/autocomplete/", {
                    params: {
                        object_type: this.objectType,
                        q: this.searchInput.trim(),
                    },
                });
                this.tagSuggestions = response.data.map(tag => ({
                    text: tag.name,
                    value: tag.name,
                    color: tag.color || '#9E9E9E'
                }));
            } catch (error) {
                console.error("Error fetching tag suggestions:", error);
                this.tagSuggestions = [];
            } finally {
                this.loading = false;
            }
        },
        onSearchInput(value) {
            if (value !== null && value !== undefined) {
                this.debouncedSearch();
            }
        },
        filterTags(item, queryText, itemText) {
            if (!queryText) return true;
            const searchText = queryText.toLowerCase();
            const itemName = typeof item === 'object' ? (item.text || item.value || item.name || '') : item;
            return itemName.toLowerCase().includes(searchText);
        },
        handleInput(value) {
            // Emit tags as objects with name and color
            let tags = Array.isArray(value)
                ? value
                    .filter(tag => {
                        if (typeof tag === 'string') return tag.trim();
                        if (typeof tag === 'object') return tag.name || tag.text || tag.value;
                        return tag;
                    })
                    .map(tag => {
                        // Convert to object with name and color
                        if (typeof tag === 'object') {
                            const tagName = tag.name || tag.text || tag.value || '';
                            const tagColor = tag.color || this.getTagColor(tag) || '#9E9E9E';
                            return {
                                name: tagName,
                                color: tagColor
                            };
                        }
                        // If it's a string, find the color from suggestions
                        const tagName = tag.trim();
                        const tagColor = this.getTagColor(tagName);
                        return {
                            name: tagName,
                            color: tagColor
                        };
                    })
                : [];

            if (!this.allowCreate) {
                const knownNames = new Set(
                    [...this.tagSuggestions, ...this.selectedTags].map((suggestion) =>
                        (suggestion.text || suggestion.value || suggestion.name || "")
                            .toLowerCase()
                    )
                );
                tags = tags.filter((tag) => knownNames.has(tag.name.toLowerCase()));
            }

            this.$emit("input", tags);
        },
        selectTag(tag) {
            // Add tag to selection if not already selected
            const tagName = this.getTagName(tag);
            const isAlreadySelected = this.selectedTags.some(t => {
                const selectedTagName = this.getTagName(t);
                return selectedTagName === tagName;
            });
            
            if (!isAlreadySelected) {
                const newTags = [...this.selectedTags, tag];
                this.handleInput(newTags);
            }
        },
        removeTag(tag) {
            const tagToRemoveName = this.getTagName(tag);
            const filteredTags = this.selectedTags.filter((t) => {
                const currentTagName = this.getTagName(t);
                return currentTagName !== tagToRemoveName;
            });
            this.handleInput(filteredTags);
        },
        getTagName(tag) {
            if (typeof tag === 'object' && tag.name) {
                return tag.name;
            }
            if (typeof tag === 'object' && tag.text) {
                return tag.text;
            }
            return tag;
        },
        getTagColor(tag) {
            if (typeof tag === 'object' && tag.color) {
                return tag.color;
            }
            // Try to find color from suggestions
            const suggestion = this.tagSuggestions.find(s => 
                (s.text || s.value || s) === this.getTagName(tag)
            );
            return suggestion?.color || '#9E9E9E';
        },
    },
    i18n: {
        messages: {
            en: {
                noTagsFound: "No tags found",
                noMatchingTags: "No matching tags",
                others: "others",
            },
            fr: {
                noTagsFound: "Aucun tag trouvé",
                noMatchingTags: "Aucun tag correspondant",
                others: "autres",
            },
        },
    },
};
</script>

<style lang="scss">
.tag-input-selection {
    display: inline-flex;
    max-width: 100%;
}
.v-select.v-select--chips.v-input--dense .v-select__selections {
    flex-wrap: wrap;
    min-height: 30px !important;
}
::v-deep .v-menu__content .v-list {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    padding: 8px;
}

::v-deep .v-menu__content .tag-suggestion-item {
    width: auto;
    min-width: auto;
    padding: 0;
    margin: 0;
    flex: 0 0 auto;
}

::v-deep .v-menu__content .tag-suggestion-item .v-list-item__content {
    padding: 0;
}
</style>

